import React from 'react'
import { useTranslation } from 'react-i18next'
import { BeWizrMediaItem } from '~/models'
import { memo } from '~/ui/component'
import { Grid, List, ListBar, SVG, Tappable, useLightbox, VBox } from '~/ui/components'
import { createUseStyles, layout } from '~/ui/styling'
import BeWizrSection from '../bewizr/BeWizrSection'
import { ImageView } from '../media'

export interface Props {
  item: BeWizrMediaItem
}

const BeWizrMediaItemView = memo('BeWizrMediaItemView', (props: Props) => {

  const {item} = props

  const [t] = useTranslation('bewizr-misc-items')

  const {setContents} = useLightbox()

  React.useEffect(() => {
    setContents(item.images.map(image => ({
      type:  'image',
      image:  {type: 'remote', url: image},
      alt:    null,
    })))
  }, [item.images, setContents])

  //------
  // Rendering

  function render() {
    return (
      <>
        {renderGallery()}
        {renderAttachments()}
      </>
    )
  }

  function renderGallery() {
    if (item.images.length === 0) { return null }

    return (
      <BeWizrSection title={t('media.gallery')}>
        <Grid columns='1fr 1fr' gap={layout.padding.s}>
          {item.images.map(renderImage)}
        </Grid>
      </BeWizrSection>
    )
  }

  function renderAttachments() {
    if (item.attachments.length === 0) { return null }
    return (
      <BeWizrSection title={t('media.attachments')}>
        <List
          data={item.attachments}
          renderItem={renderAttachment}
          itemGap={layout.padding.xs}
          flex={false}
        />
      </BeWizrSection>
    )
  }

  function renderImage(image: string, index: number) {
    return (
      <BeWizrGalleryImage key={image} image={image} index={index}/>
    )
  }

  function renderAttachment(attachment: string) {
    return (
      <BeWizrAttachmentRow key={attachment} attachment={attachment}/>
    )
  }

  return render()

})

export default BeWizrMediaItemView

const useStyles = createUseStyles(theme => ({
  image: {
    backgroundColor: theme.colors.grayScale.light[1],
    borderRadius:    layout.radius.m,
    aspectRatio:     '2/1',
    ovefrflow:       'hidden',
  },
}))

interface BeWizrAttachmentRowProps {
  attachment: string
}

const BeWizrAttachmentRow = memo('BeWizrAttachmentRow', (props: BeWizrAttachmentRowProps) => {

  const {attachment} = props

  const fileName = attachment.split('/').pop()

  //------
  // Rendering

  function render() {
    return (
      <ListBar
        image={<SVG name='page' size={layout.icon.m}/>}
        caption={fileName}
        href={attachment}
      />
    )
  }

  return render()

})

interface BeWizrGalleryImageProps {
  image: string
  index: number
}

const BeWizrGalleryImage = memo('BeWizrGalleryImage', (props: BeWizrGalleryImageProps) => {

  const {image, index} = props

  const {show} = useLightbox()

  const tap = React.useCallback(() => {
    show(index)
  }, [index, show])

  //------
  // Rendering

  const $ = useStyles()

  function render() {
    return (
      <VBox flex classNames={$.image} align='center' justify='middle'>
        <Tappable onTap={tap} flex>
          <ImageView
            source={image}
            objectFit='contain'
            flex
          />
        </Tappable>
      </VBox>
    )
  }

  return render()

})